import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext';
import Footer from '../Footer';
import './ServicePage.css';

const PolitistikiKlironomia = () => {
  const navigate = useNavigate();
  const { t, tArray, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const useCasesEl = [
    {
      title: "Ancient Manuscript Transcription",
      description: "PalaioScript AI transcribes handwritten manuscripts in multiple historical scripts — Greek, Arabic, Syriac, Russian, Byzantine. Tested on 1M+ manuscripts at Vatopedi Monastery, Mount Athos."
    },
    {
      title: "Multilingual OCR for Historical Documents",
      description: "Specialised visual language models for older scripts that standard OCR cannot read — Byzantine Greek, Ottoman Turkish, Church Slavonic. Built in collaboration with Transkribus/READ-COOP (Austria)."
    },
    {
      title: "Searchable Digital Archives",
      description: "Transform fragile physical collections into fully searchable digital repositories. Researchers can query across manuscripts, languages and centuries without handling originals."
    },
    {
      title: "Audio Heritage Transcription",
      description: "Convert historical audio recordings — oral histories, liturgical chants, interviews — into searchable text using Whisper-based AI with 98.9% accuracy across 100+ languages."
    },
    {
      title: "AI-Powered Cataloguing & Metadata",
      description: "Automatically classify, tag and enrich collections with structured metadata. Connect objects across collections using semantic search and knowledge graphs."
    },
    {
      title: "Artwork Condition Analysis",
      description: "Computer vision for detecting damage, wear and restoration needs in paintings, icons and manuscripts — enabling preventive conservation."
    }
  ];

  const faqsEl = [
    {
      question: "What makes PalaioScript AI different from standard OCR?",
      answer: "Standard OCR fails on handwritten historical scripts. PalaioScript AI uses specialised visual language models trained on real manuscript data from Mount Athos — handling ink degradation, irregular spacing, and scripts that no commercial tool supports."
    },
    {
      question: "Which scripts and languages do you support?",
      answer: "Greek (ancient, Byzantine, modern), Arabic, Syriac, Russian, Church Slavonic, Ottoman Turkish. We are expanding to Armenian and Georgian in collaboration with European research partners."
    },
    {
      question: "Do you work with religious institutions?",
      answer: "Yes. We are the Official AI Partner of the Holy Community of Mount Athos — the autonomous monastic community of 20 monasteries. We understand the sensitivity, tradition and governance requirements of sacred collections."
    },
    {
      question: "Can this work on-premise?",
      answer: "Yes. All systems can run on local infrastructure with no cloud dependency — critical for institutions that cannot send sensitive cultural data to external servers."
    },
    {
      question: "Is there EU funding available for digitisation projects?",
      answer: "Yes. We have experience with Horizon Europe cultural heritage calls and can help institutions apply for EU co-funded digitisation projects. We are currently coordinating a Horizon Europe consortium (IANUS/MUSEION) that includes Transkribus/READ-COOP for manuscript digitisation."
    }
  ];

  const serviceKey = 'servicePage.politistikiKlironomia';
  const hasTranslation = language !== 'el';

  const title = hasTranslation ? t(`${serviceKey}.title`) : 'AI for Cultural Heritage';
  const subtitle = hasTranslation ? t(`${serviceKey}.subtitle`) : 'Preserve the past. Make it searchable. Connect it to the future.';
  const description = hasTranslation ? t(`${serviceKey}.description`) : 'We build AI systems that help museums, archives, monasteries and cultural institutions digitize, transcribe and unlock access to humanity\'s written heritage — across languages, scripts and centuries.';
  const trackRecord = hasTranslation ? t(`${serviceKey}.trackRecord`) : 'Official AI Partner of the Holy Community of Mount Athos. PalaioScript AI has been tested at Vatopedi Monastery on 1M+ manuscripts in Arabic, Syriac, Russian and Greek — some dating back over 1,000 years.';

  const translatedUseCases = hasTranslation ? tArray(`${serviceKey}.useCases`) : [];
  const translatedFaqs = hasTranslation ? tArray(`${serviceKey}.faqs`) : [];

  const displayUseCases = translatedUseCases.length > 0 ? translatedUseCases : useCasesEl;
  const displayFaqs = translatedFaqs.length > 0 ? translatedFaqs : faqsEl;

  const ctaTitle = hasTranslation ? t(`${serviceKey}.ctaTitle`) : 'Διαφυλάξτε την Πολιτιστική Κληρονομιά με AI';
  const ctaText = hasTranslation ? t(`${serviceKey}.ctaText`) : 'Επικοινωνήστε μαζί μας για να συζητήσουμε πώς η AI μπορεί να υποστηρίξει τα πολιτιστικά και θρησκευτικά σας έργα.';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={hasTranslation ? description : "Ψηφιοποίηση χειρογράφων Αγίου Όρους και ιερών κειμένων με AI. Μεταγραφή ηχογραφήσεων, κατηγοριοποίηση μουσειακών συλλογών. On-premise & GDPR."} />
        <link rel="canonical" href="https://ai-agency.gr/services/politistiki-klironomia" />
        
        {/* Schema.org Service Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": hasTranslation ? title : "AI για Πολιτιστική Κληρονομιά",
            "provider": {
              "@type": "Organization",
              "name": "PROTOS AI AGENCY",
              "url": "https://ai-agency.gr/"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Greece"
            },
            "description": hasTranslation ? description : "Ψηφιοποίηση χειρογράφων Αγίου Όρους και ιερών κειμένων. Μεταγραφή ηχογραφήσεων, κατηγοριοποίηση μουσειακών συλλογών με AI."
          })}
        </script>

        {/* Schema.org FAQPage Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": displayFaqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": hasTranslation ? t('servicePage.breadcrumb.home') : "Αρχική",
                "item": "https://ai-agency.gr/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": hasTranslation ? t('servicePage.breadcrumb.services') : "Υπηρεσίες",
                "item": "https://ai-agency.gr/#services"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Πολιτιστική Κληρονομιά",
                "item": "https://ai-agency.gr/services/politistiki-klironomia"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="service-page">
        <header className="service-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button 
              onClick={() => navigate('/')}
              className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-2 mb-4"
            >
              <span>←</span>
              <span>{t('nav.backToHome')}</span>
            </button>
            
            {/* Breadcrumb Navigation */}
            <nav aria-label="breadcrumb" className="text-sm text-gray-400 mb-2">
              <ol className="flex items-center space-x-2">
                <li>
                  <a href="/" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.home')}</a>
                </li>
                <li><span>/</span></li>
                <li>
                  <a href="/#services" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.services')}</a>
                </li>
                <li><span>/</span></li>
                <li className="text-gray-300" aria-current="page">{hasTranslation ? title : "Πολιτιστική Κληρονομιά"}</li>
              </ol>
            </nav>
          </div>
        </header>

        <section className="service-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>
        </section>

        <section className="service-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="prose prose-lg prose-invert max-w-none">
              {hasTranslation ? (
                <p className="text-lg text-gray-300 leading-relaxed">
                  {description}
                </p>
              ) : (
                <>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Η πολιτιστική κληρονομιά της Ελλάδας αποτελεί θησαυρό ανεκτίμητης αξίας για την ανθρωπότητα. Χειρόγραφα αιώνων, ιστορικά αρχεία, σπάνιες ηχογραφήσεις και καλλιτεχνικά έργα χρειάζονται προστασία, διατήρηση και προσβασιμότητα για τις μελλοντικές γενιές. Η Τεχνητή Νοημοσύνη προσφέρει καινοτόμες λύσεις που κάνουν αυτή την αποστολή όχι μόνο εφικτή, αλλά και αποτελεσματική.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Ειδικευόμαστε στην ψηφιοποίηση και διατήρηση πολιτιστικής κληρονομιάς, με ιδιαίτερη εμπειρία στη συνεργασία με ιερά ιδρύματα, όπως τα μοναστήρια του Αγίου Όρους, μουσεία, βιβλιοθήκες και πολιτιστικούς οργανισμούς. Κατανοούμε τη σημασία του σεβασμού στην παράδοση και την ευαισθησία που απαιτείται στη διαχείριση ιερών και ιστορικών τεκμηρίων.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Τα συστήματά μας χρησιμοποιούν προηγμένες τεχνολογίες OCR (Optical Character Recognition) που έχουν εκπαιδευτεί ειδικά για την αναγνώριση ελληνικών χειρογράφων, συμπεριλαμβανομένων παλαιών γραφών και διαφορετικών καλλιγραφικών στυλ. Αυτό επιτρέπει την αυτόματη μετατροπή χειρόγραφων κειμένων σε ψηφιακή μορφή με υψηλή ακρίβεια, διατηρώντας παράλληλα το πρωτότυπο υλικό ασφαλές.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Για το ηχητικό υλικό, χρησιμοποιούμε την τεχνολογία Whisper AI της OpenAI, προσαρμοσμένη για την ελληνική γλώσσα, για την αυτόματη μεταγραφή ιστορικών ηχογραφήσεων, προφορικών παραδόσεων και μουσικών αρχείων. Αυτό καθιστά το υλικό αναζητήσιμο και προσβάσιμο σε ερευνητές και το ευρύ κοινό.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Η τεχνολογία ανάλυσης εικόνας μας επιτρέπει την αυτόματη κατηγοριοποίηση και περιγραφή φωτογραφιών, πινάκων και άλλων οπτικών τεκμηρίων, δημιουργώντας πλούσια metadata που διευκολύνουν την έρευνα και την ανακάλυψη. Συστήματα AI μπορούν επίσης να ανιχνεύσουν φθορές σε έργα τέχνης και να προτείνουν μεθόδους συντήρησης.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Μια από τις πιο σημαντικές εφαρμογές μας είναι η συνεργασία με το Άγιο Όρος για την ψηφιοποίηση σπάνιων χειρογράφων και την προστασία της μοναστικής κληρονομιάς. Με σεβασμό στην ιερότητα του χώρου και τις παραδόσεις των μοναστηριών, αναπτύσσουμε λύσεις που επιτρέπουν την πρόσβαση στη γνώση χωρίς να θέτουν σε κίνδυνο τα πρωτότυπα τεκμήρια.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Η διατήρηση της πολιτιστικής κληρονομιάς δεν είναι μόνο τεχνική πρόκληση—είναι ηθική υποχρέωση. Με τη χρήση της Τεχνητής Νοημοσύνης, διασφαλίζουμε ότι οι θησαυροί του παρελθόντος θα παραμείνουν ζωντανοί και προσβάσιμοι για τις γενιές του μέλλοντος.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="use-cases-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {t('servicePage.useCasesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayUseCases.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <h3 className="text-xl font-bold mb-3 text-purple-400">{useCase.title}</h3>
                  <p className="text-gray-300">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-black/30 relative z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {t('servicePage.faqTitle')}
            </h2>
            <div className="space-y-6">
              {displayFaqs.map((faq, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3 text-purple-400">{faq.question}</h3>
                  <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="py-16 relative z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-8 text-white">
              {t('servicePage.relatedServicesTitle')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <a href="/services/dimosia-dioikisi" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-400/40 transition-all">
                <h3 className="text-xl font-bold mb-2 text-blue-400">{t('services.dimosiaDioikisi')}</h3>
                <p className="text-gray-400">{t('servicePage.relatedDescriptions.dimosiaDioikisi')}</p>
              </a>
              <a href="/services/erevna-anaptyxi" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-cyan-400/40 transition-all">
                <h3 className="text-xl font-bold mb-2 text-cyan-400">{t('services.erevnaAnaptyxi')}</h3>
                <p className="text-gray-400">{t('servicePage.relatedDescriptions.erevnaAnaptyxi')}</p>
              </a>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              {ctaTitle}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              {ctaText}
            </p>
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105"
            >
              {t('servicePage.ctaButton')}
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PolitistikiKlironomia;
